'use client';

import { FormEvent, useEffect, useState } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { cleanPhoneNumber } from '@/services/phone';
import {
  sendRegistrationEmailOtp,
  type SendRegistrationEmailOtpBody,
  verifyRegistrationEmailOtp
} from '@/services/webApi';
import {
  partnerInterestFormSchema,
  type PartnerInterestFormValues
} from './partnerRegistrationSchema';
import { useOtpForm } from '@/app/partner/components/OtpForm/useOtpForm';

export type RegistrationStep = 'form' | 'verify' | 'success';

const defaultFormValues: DefaultValues<PartnerInterestFormValues> = {
  organizationName: '',
  slug: '',
  websiteUrl: '',
  einNumber: '',
  organizationType: '',
  streetAddress: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  hasMultipleLocations: undefined,
  numberOfLocations: undefined,
  networkDenominationAssociation: undefined,
  networkDenomination: '',
  participationInterests: [],
  participationInterestGoodsServices: '',
  languagePreference: '',
  languagePreferenceOther: '',
  referralSource: '',
  referralSourceOther: '',
  privacyPolicyAccepted: false
};

type UsePartnerSignUpOptions = {
  onStepChange?: (step: RegistrationStep) => void;
};

export function usePartnerSignUp({
  onStepChange
}: UsePartnerSignUpOptions = {}) {
  const [step, setStep] = useState<RegistrationStep>('form');
  const [pendingValues, setPendingValues] =
    useState<SendRegistrationEmailOtpBody | null>(null);
  const {
    otpValue,
    otpVerificationError,
    setOtpVerificationError,
    handleOtpChange,
    resetOtp,
    otpLength
  } = useOtpForm();

  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  const form = useForm<PartnerInterestFormValues>({
    resolver: zodResolver(partnerInterestFormSchema),
    defaultValues: defaultFormValues,
    mode: 'onBlur',
    reValidateMode: 'onChange'
  });

  const sendOtpMutation = useMutation({
    mutationFn: sendRegistrationEmailOtp
  });

  const verifyOtpMutation = useMutation({
    mutationFn: verifyRegistrationEmailOtp
  });

  const formErrorMessage =
    sendOtpMutation.isError && sendOtpMutation.error instanceof Error
      ? sendOtpMutation.error.message
      : null;

  const onSubmit = async (values: PartnerInterestFormValues) => {
    setOtpVerificationError(null);

    const { networkDenominationAssociation, networkDenomination, ...rest } =
      values;

    const sanitizedValues = {
      ...rest,
      networkDenomination:
        networkDenominationAssociation === 'No'
          ? 'No'
          : (networkDenomination ?? '').trim(),
      email: values.email.toLowerCase(),
      phone: cleanPhoneNumber(values.phone)
    };

    try {
      await sendOtpMutation.mutateAsync(sanitizedValues);
      setPendingValues(sanitizedValues);
      resetOtp();
      setStep('verify');
    } catch (_error) {
      // Errors surface via formErrorMessage
    }
  };

  const handleVerificationSubmit = async (
    event: FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (!pendingValues) return;

    const code = otpValue;
    if (code.length !== otpLength) {
      setOtpVerificationError('Enter the 6-digit code to continue.');
      return;
    }

    try {
      setOtpVerificationError(null);
      await verifyOtpMutation.mutateAsync({
        email: pendingValues.email.toLowerCase(),
        code
      });

      form.reset(defaultFormValues);
      setPendingValues(null);
      setStep('success');
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Verification failed. Please try again.';
      setOtpVerificationError(message);
    }
  };

  const handleResend = async (): Promise<boolean> => {
    if (!pendingValues) return false;
    resetOtp();
    try {
      setOtpVerificationError(null);
      await sendOtpMutation.mutateAsync(pendingValues);
      return true;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to resend the code right now.';
      setOtpVerificationError(message);
      return false;
    }
  };

  return {
    form,
    step,
    pendingValues,
    otpValue,
    otpLength,
    otpVerificationError,
    formErrorMessage,
    isSendOtpPending: sendOtpMutation.isPending,
    isVerifyOtpPending: verifyOtpMutation.isPending,
    onSubmit,
    handleOtpChange,
    handleVerificationSubmit,
    handleResend
  };
}
